import { useQuery, useMutation, createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import {
  listEmailTemplates,
  getEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplateCategories,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListEmailTemplatesRequestSchema,
  ListEmailTemplatesResponseSchema,
  GetEmailTemplateRequestSchema,
  GetEmailTemplateResponseSchema,
  GetEmailTemplateCategoriesRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { authStore } from './auth.store'

/**
 * Hook to fetch all email templates for an organization
 */
export function useEmailTemplates(organizationId: string) {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    listEmailTemplates,
    create(ListEmailTemplatesRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    templates: query.data?.emailTemplates || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a single email template by ID
 */
export function useEmailTemplate(templateId: string) {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    getEmailTemplate,
    create(GetEmailTemplateRequestSchema, { id: templateId }),
    {
      enabled: isAuthenticated && !!templateId,
    }
  )

  return {
    template: query.data?.emailTemplate || null,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch available template categories
 */
export function useEmailTemplateCategories() {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    getEmailTemplateCategories,
    create(GetEmailTemplateCategoriesRequestSchema, {}),
    {
      enabled: isAuthenticated,
      staleTime: 60 * 60 * 1000, // Categories rarely change, cache for 1 hour
    }
  )

  return {
    categories: query.data?.categories || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
  }
}

/**
 * Hook to create a new email template
 */
export function useCreateEmailTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createEmailTemplate, {
    onSuccess: (response) => {
      const createdTemplate = response.emailTemplate
      if (!createdTemplate) return

      // Update individual template cache
      const queryKey = createConnectQueryKey({
        schema: getEmailTemplate,
        transport,
        input: { id: createdTemplate.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetEmailTemplateResponseSchema, { emailTemplate: createdTemplate })
      )

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an existing email template
 */
export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateEmailTemplate, {
    onSuccess: (response) => {
      const updatedTemplate = response.emailTemplate
      if (!updatedTemplate) return

      // Update specific template cache
      const templateQueryKey = createConnectQueryKey({
        schema: getEmailTemplate,
        transport,
        input: { id: updatedTemplate.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        templateQueryKey,
        create(GetEmailTemplateResponseSchema, { emailTemplate: updatedTemplate })
      )

      // Update ALL list caches instantly
      const listQueryFilter = createConnectQueryKey({
        schema: listEmailTemplates,
        transport,
        cardinality: undefined,
      })

      // Get all active list queries
      const existingListQueries = queryClient.getQueriesData<{ emailTemplates: typeof updatedTemplate[] }>({
        queryKey: listQueryFilter,
      })

      // Update each list with the updated template
      existingListQueries.forEach(([queryKey, oldData]) => {
        if (!oldData?.emailTemplates) return

        // Immutably update the template in this list
        const updatedTemplates = oldData.emailTemplates.map((template) =>
          template.id === updatedTemplate.id ? updatedTemplate : template
        )

        // Set updated list back to cache
        queryClient.setQueryData(
          queryKey,
          create(ListEmailTemplatesResponseSchema, {
            emailTemplates: updatedTemplates,
          })
        )
      })

      // Background refetch for server consistency
      queryClient.invalidateQueries({
        queryKey: listQueryFilter,
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to delete an email template
 */
export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteEmailTemplate, {
    onSuccess: (_response, variables) => {
      // Remove from individual cache
      queryClient.removeQueries({
        queryKey: createConnectQueryKey({
          schema: getEmailTemplate,
          transport,
          input: { id: variables.id },
          cardinality: 'finite',
        }),
      })

      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
